(() => {
  const schemeMap = window.themeColorSchemeMap || {};
  const root = document.documentElement;
  const activeSources = new Map();
  const sourceCleanups = new WeakMap();
  const viewportSources = new Map();
  const viewportThresholds = [0, 0.15, 0.33, 0.5, 0.66, 0.85, 1];
  const runtimeVarNames = [
    '--color-background',
    '--color-background-secondary',
    '--color-background-gradient',
    '--color-foreground',
    '--color-text',
    '--color-subtext',
    '--color-heading',
    '--color-link-hover',
    '--button-text-color-primary',
    '--button-border-color-primary',
    '--button-bg-color-primary',
    '--button-hover-text-color-primary',
    '--button-hover-border-color-primary',
    '--button-hover-bg-color-primary',
    '--color-button-outline',
    '--button-text-color-underline',
    '--button-hover-text-color-underline',
  ];
  const runtimeVarAliasMap = {
    '--color-background': '--theme-runtime-color-background',
    '--color-background-secondary': '--theme-runtime-color-background-secondary',
    '--color-background-gradient': '--theme-runtime-color-background-gradient',
    '--color-foreground': '--theme-runtime-color-foreground',
    '--color-text': '--theme-runtime-color-text',
    '--color-subtext': '--theme-runtime-color-subtext',
    '--color-heading': '--theme-runtime-color-heading',
    '--color-link-hover': '--theme-runtime-color-link-hover',
    '--button-text-color-primary': '--theme-runtime-button-text-color-primary',
    '--button-border-color-primary': '--theme-runtime-button-border-color-primary',
    '--button-bg-color-primary': '--theme-runtime-button-bg-color-primary',
    '--button-hover-text-color-primary': '--theme-runtime-button-hover-text-color-primary',
    '--button-hover-border-color-primary': '--theme-runtime-button-hover-border-color-primary',
    '--button-hover-bg-color-primary': '--theme-runtime-button-hover-bg-color-primary',
    '--color-button-outline': '--theme-runtime-color-button-outline',
    '--button-text-color-underline': '--theme-runtime-button-text-color-underline',
    '--button-hover-text-color-underline': '--theme-runtime-button-hover-text-color-underline',
  };

  let defaultsCaptured = false;
  let sequence = 0;
  let viewportObserver = null;
  let viewportSourceId = null;
  let viewportEvaluationFrame = null;
  let viewportListenersBound = false;
  const defaultVars = {};

  const captureDefaults = () => {
    if (defaultsCaptured) return;

    const computed = window.getComputedStyle(root);
    runtimeVarNames.forEach((varName) => {
      defaultVars[varName] = computed.getPropertyValue(varName).trim();
    });
    defaultsCaptured = true;
  };

  const applyVars = (vars) => {
    captureDefaults();

    runtimeVarNames.forEach((varName) => {
      const value = vars && Object.prototype.hasOwnProperty.call(vars, varName) ? vars[varName] : defaultVars[varName];
      if (value && value !== '') {
        root.style.setProperty(varName, value);
        root.style.setProperty(runtimeVarAliasMap[varName], value);
      } else {
        root.style.removeProperty(varName);
        root.style.removeProperty(runtimeVarAliasMap[varName]);
      }
    });

    const background = (vars && vars['--color-background']) || defaultVars['--color-background'];
    const gradient = (vars && vars['--color-background-gradient']) || defaultVars['--color-background-gradient'];

    if (background && background !== '') {
      root.style.setProperty('--theme-runtime-header-bg', background);
    } else {
      root.style.removeProperty('--theme-runtime-header-bg');
    }

    if (gradient && gradient !== '') {
      root.style.setProperty('--theme-runtime-header-gradient', gradient);
    } else {
      root.style.setProperty('--theme-runtime-header-gradient', 'none');
    }
  };

  const getTopActiveEntry = () => {
    let topEntry = null;

    activeSources.forEach((entry) => {
      if (!topEntry) {
        topEntry = entry;
        return;
      }

      if (entry.priority > topEntry.priority) {
        topEntry = entry;
        return;
      }

      if (entry.priority === topEntry.priority && entry.order > topEntry.order) {
        topEntry = entry;
      }
    });

    return topEntry;
  };

  const refresh = () => {
    const topEntry = getTopActiveEntry();

    if (!topEntry) {
      root.classList.remove('theme-runtime-color-active');
      applyVars(null);
      return;
    }

    const scheme = schemeMap[topEntry.schemeId];
    if (!scheme) {
      activeSources.delete(topEntry.sourceId);
      refresh();
      return;
    }

    root.classList.add('theme-runtime-color-active');
    applyVars(scheme);
  };

  const api = {
    activate(sourceId, schemeId, priority = 0) {
      if (!sourceId || !schemeId) return;

      activeSources.set(sourceId, {
        sourceId,
        schemeId,
        priority: Number(priority) || 0,
        order: ++sequence,
      });
      refresh();
    },
    deactivate(sourceId) {
      if (!sourceId) return;

      if (activeSources.delete(sourceId)) {
        refresh();
      }
    },
    reset() {
      activeSources.clear();
      viewportSourceId = null;
      refresh();
    },
    refreshDefaults() {
      defaultsCaptured = false;
      captureDefaults();
      refresh();
    },
  };

  window.ThemeRuntimeColorTransition = api;

  const scheduleViewportEvaluation = () => {
    if (viewportEvaluationFrame) return;

    viewportEvaluationFrame = window.requestAnimationFrame(() => {
      viewportEvaluationFrame = null;
      evaluateViewportSources();
    });
  };

  const evaluateViewportSources = () => {
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 1;
    let bestState = null;
    let bestDistance = Infinity;
    let bestVisibleRatio = -1;

    viewportSources.forEach((state) => {
      const element = state.element;
      if (!element || !element.isConnected) return;

      const rect = element.getBoundingClientRect();
      const visibleTop = Math.max(rect.top, 0);
      const visibleBottom = Math.min(rect.bottom, viewportHeight);
      const visibleHeight = Math.max(0, visibleBottom - visibleTop);

      if (visibleHeight <= 0 || rect.height <= 0) return;

      const elementCenter = rect.top + rect.height / 2;
      const distance = Math.abs(elementCenter - viewportHeight / 2);
      const visibleRatio = visibleHeight / Math.max(rect.height, 1);
      const visibleThreshold = Math.max(0, Math.min(1, Number(state.visibleThreshold) || 0));

      if (visibleRatio < visibleThreshold) return;

      if (
        !bestState ||
        distance < bestDistance - 1 ||
        (Math.abs(distance - bestDistance) <= 1 && visibleRatio > bestVisibleRatio)
      ) {
        bestState = state;
        bestDistance = distance;
        bestVisibleRatio = visibleRatio;
      }
    });

    const nextSourceId = bestState ? bestState.sourceId : null;

    if (nextSourceId === viewportSourceId) return;

    if (viewportSourceId) {
      api.deactivate(viewportSourceId);
    }

    viewportSourceId = nextSourceId;

    if (bestState) {
      api.activate(bestState.sourceId, bestState.schemeId, bestState.priority || 0);
    }
  };

  const ensureViewportObserver = () => {
    if (viewportObserver || !('IntersectionObserver' in window)) return;

    viewportObserver = new window.IntersectionObserver(
      () => {
        scheduleViewportEvaluation();
      },
      {
        threshold: viewportThresholds,
      }
    );
  };

  const bindViewportListeners = () => {
    if (viewportListenersBound) return;

    viewportListenersBound = true;
    window.addEventListener('scroll', scheduleViewportEvaluation, { passive: true });
    window.addEventListener('resize', scheduleViewportEvaluation);
    window.addEventListener('orientationchange', scheduleViewportEvaluation);
    window.addEventListener('load', scheduleViewportEvaluation);
    window.addEventListener('pageshow', scheduleViewportEvaluation);
    document.addEventListener('theme:layout:changed', scheduleViewportEvaluation);
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(scheduleViewportEvaluation).catch(() => {});
    }
  };

  const initializeSectionSources = (scope = document) => {
    const targets = [];
    if (scope.matches && scope.matches('[data-runtime-color-source]')) {
      targets.push(scope);
    }
    if (scope.querySelectorAll) {
      targets.push(...scope.querySelectorAll('[data-runtime-color-source]'));
    }

    targets.forEach((element) => {
      if (element.dataset.runtimeColorSourceInitialized === 'true') return;

      const schemeId = element.dataset.runtimeColorScheme || '';
      const enabled = element.dataset.runtimeColorTransition === 'true' && schemeId !== '';

      if (!enabled) {
        element.dataset.runtimeColorSourceInitialized = 'true';
        return;
      }

      const sourceId = element.dataset.runtimeColorSourceId || element.id || `runtime-color-${Math.random().toString(36).slice(2)}`;
      const priority = Number(element.dataset.runtimeColorPriority || 0) || 0;
      const visibleThreshold = Number(element.dataset.runtimeColorVisibleThreshold || 0) || 0;

      viewportSources.set(element, {
        element,
        sourceId,
        schemeId,
        priority,
        visibleThreshold,
      });

      ensureViewportObserver();
      bindViewportListeners();

      if (viewportObserver) {
        viewportObserver.observe(element);
      }

      sourceCleanups.set(element, () => {
        if (viewportObserver) {
          viewportObserver.unobserve(element);
        }

        viewportSources.delete(element);
        api.deactivate(sourceId);

        if (viewportSourceId === sourceId) {
          viewportSourceId = null;
        }

        scheduleViewportEvaluation();
      });

      element.dataset.runtimeColorSourceInitialized = 'true';
    });

    scheduleViewportEvaluation();
  };

  const unloadSectionSources = (scope) => {
    if (!scope) return;

    const targets = [];
    if (scope.matches && scope.matches('[data-runtime-color-source]')) {
      targets.push(scope);
    }
    if (scope.querySelectorAll) {
      targets.push(...scope.querySelectorAll('[data-runtime-color-source]'));
    }

    targets.forEach((element) => {
      const cleanup = sourceCleanups.get(element);
      if (cleanup) cleanup();
      sourceCleanups.delete(element);
      delete element.dataset.runtimeColorSourceInitialized;
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        captureDefaults();
        initializeSectionSources(document);
      },
      { once: true }
    );
  } else {
    captureDefaults();
    initializeSectionSources(document);
  }

  document.addEventListener('shopify:section:load', (event) => {
    initializeSectionSources(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    unloadSectionSources(event.target);
  });
})();
